import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { FaPaperPlane } from 'react-icons/fa';
import './EmailModal.css';

export default function BulkEmailModal({
  open,
  title = 'Send Email',
  initialSubject = '',
  initialMessage = '',
  note = null,
  onClose,
  onSend,
  confirmText = 'Send Email',
  cancelText = 'Cancel',
}) {
  const [subject, setSubject] = useState(initialSubject);
  const [message, setMessage] = useState(initialMessage);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const subjectRef = useRef(null);
  const formId = useId();

  useEffect(() => {
    if (!open) return;
    setSubject(initialSubject || '');
    setMessage(initialMessage || '');
    setError('');
    setTimeout(() => { try { subjectRef.current?.focus(); } catch {} }, 0);
  }, [open, initialSubject, initialMessage]);

  const handleOverlayClick = (e) => {
    if (e.target.classList.contains('eml-modal__overlay')) onClose?.();
  };

  const handleSend = useCallback(async () => {
    setError('');
    const subj = String(subject || '').trim();
    const msg = String(message || '').trim();
    if (!subj) { setError('Subject is required.'); return; }
    if (!msg) { setError('Message is required.'); return; }

    setSending(true);
    try {
      await onSend?.({ subject: subj, message: msg });
      onClose?.();
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Failed to send email.');
    } finally {
      setSending(false);
    }
  }, [subject, message, onSend, onClose]);

  if (!open) return null;

  return (
    <div
      className="eml-modal__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="bulk-email-modal-title"
      onClick={handleOverlayClick}
    >
      <div className="eml-modal">
        <div className="eml-modal__header">
          <h2 id="bulk-email-modal-title">{title}</h2>
          <button
            className="eml-modal__close"
            onClick={() => onClose?.()}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="eml-modal__body">
          {error ? <div className="eml-modal__error" role="alert">{error}</div> : null}

          <form
            id={formId}
            className="eml-form"
            autoComplete="on"
            onSubmit={(e) => { e.preventDefault(); handleSend(); }}
          >
            <label>
              <span>Subject</span>
              <input
                ref={subjectRef}
                type="text"
                placeholder="Subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </label>

            <label>
              <span>Message</span>
              <textarea
                rows={10}
                placeholder="Write your message…"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </label>

            {note ? (
              <div style={{ fontSize: 13, opacity: 0.9 }}>
                {note}
              </div>
            ) : null}
          </form>
        </div>

        <div className="eml-modal__footer">
          <button className="btn" onClick={() => onClose?.()} disabled={sending}>
            {cancelText}
          </button>
          <button
            className="btn primary"
            form={formId}
            onClick={handleSend}
            disabled={sending}
          >
            <FaPaperPlane style={{ marginRight: 8 }} />
            {sending ? 'Sending…' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

